import React, { JSX, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./carouselSlider.module.css";

interface MediaItem {
  src: string;
  alt: string;
  type: "image" | "video";
}

interface CarouselSliderProps {
  images: MediaItem[];
}

const CarouselSlider: React.FC<CarouselSliderProps> = ({ images }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1300);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [currentIndex, setCurrentIndex] = useState(1);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const getImageAt = (offset: number) => {
    const index = (currentIndex + offset + images.length) % images.length;
    return images[index];
  };

  const renderMedia = (
    media: MediaItem,
    isActive: boolean = false
  ): JSX.Element => {
    const commonClass = isActive
      ? `${styles.sliderImage} ${styles.active}`
      : styles.sliderImage;

    if (media.type === "image") {
      return (
        <img
          src={media.src}
          alt={media.alt}
          className={commonClass}
        />
      );
    }

    return (
      <div className={`${styles.videoContainer} ${isActive ? styles.active : ""}`}>
        <iframe
          src={media.src}
          title={media.alt}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  };

  return (
    <div className={styles.carouselSliderWrapper}>
      {!isMobile && (
        <button
          onClick={prevSlide}
          className={`${styles.arrowButton} ${styles.arrowLeft}`}
        >
          <ChevronLeft size={32} />
        </button>
      )}

      <div
        className={styles.sliderImages}
        style={{ gap: isMobile ? "0px" : "16px" }}
      >
        {renderMedia(getImageAt(-1))}

        {isMobile && (
          <button
            style={{ zIndex: 4, transform: "translateX(50%)" }}
            onClick={prevSlide}
            className={`${styles.arrowButton} ${styles.arrowLeft}`}
          >
            <ChevronLeft size={32} />
          </button>
        )}

        {renderMedia(getImageAt(0), true)}

        {isMobile && (
          <button
            style={{ zIndex: 4, transform: "translateX(-50%)" }}
            onClick={nextSlide}
            className={`${styles.arrowButton} ${styles.arrowRight}`}
          >
            <ChevronRight size={32} />
          </button>
        )}

        {renderMedia(getImageAt(1))}
      </div>

      {!isMobile && (
        <button
          onClick={nextSlide}
          className={`${styles.arrowButton} ${styles.arrowRight}`}
        >
          <ChevronRight size={32} />
        </button>
      )}
    </div>
  );
};

export default CarouselSlider;
